// Kindle-style text highlighting.
//
// Attaches to every element carrying `data-highlightable` (the content
// containers rendered in regular, book, and slideshow modes). On load it
// re-applies the profile's saved highlights; on selection it offers a popover
// to save a colored highlight with an optional note. Anonymous readers are
// nudged to sign in instead of saving.

import { buildIndex, describeRange, locate, wrapOffsets } from "./anchor";

const COLORS = {
  yellow: "#fff3a3",
  green: "#b8f0c2",
  blue: "#b6ddff",
  pink: "#ffc2dd",
};
const DEFAULT_COLOR = "yellow";

function colorValue(name) {
  return COLORS[name] || COLORS[DEFAULT_COLOR];
}

function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute("content") : null;
}

function copyText(text) {
  // Prefer the async Clipboard API; fall back to copying the live selection.
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).catch(() => {});
    return;
  }
  try {
    document.execCommand("copy");
  } catch (e) {
    /* clipboard unavailable */
  }
}

function injectStyles() {
  if (document.getElementById("highlighter-styles")) return;
  const style = document.createElement("style");
  style.id = "highlighter-styles";
  style.textContent = `
    mark.hl { border-radius: 2px; padding: 0 1px; cursor: pointer; color: inherit; }
    mark.hl.hl-has-note { box-shadow: inset 0 -2px 0 rgba(0,0,0,.35); }
    .hl-popover {
      position: absolute; z-index: 2147483000; background: #fff; color: #222;
      border: 1px solid #d0d0d0; border-radius: 8px; box-shadow: 0 6px 24px rgba(0,0,0,.18);
      padding: 8px; font-size: 14px; line-height: 1.3; max-width: 280px;
      font-family: -apple-system, Segoe UI, Roboto, sans-serif;
    }
    .hl-popover .hl-swatches { display: flex; gap: 6px; margin-bottom: 6px; }
    .hl-popover .hl-swatch {
      width: 22px; height: 22px; border-radius: 50%; cursor: pointer;
      border: 2px solid transparent; box-sizing: border-box;
    }
    .hl-popover .hl-swatch.is-selected { border-color: #333; }
    .hl-popover textarea {
      width: 100%; box-sizing: border-box; border: 1px solid #ddd; border-radius: 6px;
      padding: 6px; font-size: 13px; resize: vertical; min-height: 34px; margin-bottom: 6px;
    }
    .hl-popover .hl-actions { display: flex; gap: 8px; justify-content: flex-end; align-items: center; }
    .hl-popover button {
      border: 0; border-radius: 6px; padding: 5px 12px; cursor: pointer; font-size: 13px;
    }
    .hl-popover .hl-save { background: #2e7d32; color: #fff; }
    .hl-popover .hl-delete { background: transparent; color: #c62828; padding: 5px 6px; }
    .hl-popover .hl-cancel { background: #eee; color: #333; }
    .hl-popover .hl-nudge a { color: #2e7d32; font-weight: 600; text-decoration: underline; }
  `;
  document.head.appendChild(style);
}

// ---- popover ---------------------------------------------------------------

let openPopover = null;

function closePopover() {
  if (openPopover && openPopover.parentNode) openPopover.parentNode.removeChild(openPopover);
  openPopover = null;
}

function positionPopover(el, rect) {
  document.body.appendChild(el);
  const top = window.scrollY + rect.bottom + 8;
  let left = window.scrollX + rect.left;
  const maxLeft = window.scrollX + document.documentElement.clientWidth - el.offsetWidth - 8;
  if (left > maxLeft) left = Math.max(window.scrollX + 8, maxLeft);
  el.style.top = `${top}px`;
  el.style.left = `${left}px`;
}

function buildSwatches(selected, onPick) {
  const wrap = document.createElement("div");
  wrap.className = "hl-swatches";
  Object.keys(COLORS).forEach((name) => {
    const dot = document.createElement("span");
    dot.className = "hl-swatch" + (name === selected ? " is-selected" : "");
    dot.style.background = COLORS[name];
    dot.dataset.color = name;
    dot.addEventListener("mousedown", (e) => {
      e.preventDefault();
      wrap.querySelectorAll(".hl-swatch").forEach((s) => s.classList.remove("is-selected"));
      dot.classList.add("is-selected");
      onPick(name);
    });
    wrap.appendChild(dot);
  });
  return wrap;
}

// Popover shown for a fresh selection (or an anonymous-reader nudge).
function showCreatePopover(ctx, rect, anchor) {
  closePopover();
  const el = document.createElement("div");
  el.className = "hl-popover";

  if (!ctx.signedIn) {
    const nudge = document.createElement("div");
    nudge.className = "hl-nudge";
    nudge.innerHTML = `Want to save this highlight? <a href="${ctx.signInUrl}">Log in to highlight</a>.`;
    el.appendChild(nudge);
    openPopover = el;
    positionPopover(el, rect);
    return;
  }

  let color = DEFAULT_COLOR;
  el.appendChild(buildSwatches(color, (name) => (color = name)));

  const note = document.createElement("textarea");
  note.placeholder = "Add a note (optional)";
  el.appendChild(note);

  const actions = document.createElement("div");
  actions.className = "hl-actions";
  const copy = document.createElement("button");
  copy.className = "hl-cancel";
  copy.textContent = "Copy";
  copy.addEventListener("mousedown", (e) => {
    e.preventDefault();
    copyText(anchor.quote);
    closePopover();
  });
  const save = document.createElement("button");
  save.className = "hl-save";
  save.textContent = "Highlight";
  save.addEventListener("mousedown", (e) => {
    e.preventDefault();
    save.disabled = true;
    createHighlight(ctx, anchor, color, note.value.trim());
  });
  actions.appendChild(copy);
  actions.appendChild(save);
  el.appendChild(actions);

  openPopover = el;
  positionPopover(el, rect);
  // Leave the selection intact so the reader can still Ctrl/Cmd+C to copy.
}

// Popover shown when clicking an existing highlight.
function showEditPopover(ctx, mark) {
  closePopover();
  const hl = ctx.byId[mark.dataset.highlightId];
  if (!hl) return;
  const el = document.createElement("div");
  el.className = "hl-popover";

  let color = hl.color || DEFAULT_COLOR;
  el.appendChild(
    buildSwatches(color, (name) => {
      color = name;
      updateHighlight(ctx, hl, { color });
    })
  );

  const note = document.createElement("textarea");
  note.placeholder = "Add a note (optional)";
  note.value = hl.note || "";
  el.appendChild(note);

  const actions = document.createElement("div");
  actions.className = "hl-actions";
  const del = document.createElement("button");
  del.className = "hl-delete";
  del.textContent = "Delete";
  del.addEventListener("mousedown", (e) => {
    e.preventDefault();
    deleteHighlight(ctx, hl);
  });
  const save = document.createElement("button");
  save.className = "hl-save";
  save.textContent = "Save note";
  save.addEventListener("mousedown", (e) => {
    e.preventDefault();
    updateHighlight(ctx, hl, { note: note.value.trim() });
    closePopover();
  });
  actions.appendChild(del);
  actions.appendChild(save);
  el.appendChild(actions);

  openPopover = el;
  positionPopover(el, mark.getBoundingClientRect());
}

// ---- rendering -------------------------------------------------------------

function applyHighlight(ctx, hl) {
  const index = buildIndex(ctx.root);
  const range = locate(index, hl);
  if (!range) return;
  const marks = wrapOffsets(ctx.root, range.start, range.end, (mark) => {
    mark.className = "hl" + (hl.note ? " hl-has-note" : "");
    mark.style.backgroundColor = colorValue(hl.color);
    mark.dataset.highlightId = hl.id;
    if (hl.note) mark.title = hl.note;
  });
  ctx.marks[hl.id] = (ctx.marks[hl.id] || []).concat(marks);
}

function removeMarks(ctx, id) {
  (ctx.marks[id] || []).forEach((mark) => {
    if (!mark.parentNode) return;
    const parent = mark.parentNode;
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
    parent.removeChild(mark);
    parent.normalize();
  });
  ctx.marks[id] = [];
}

function restyleMarks(ctx, hl) {
  (ctx.marks[hl.id] || []).forEach((mark) => {
    mark.style.backgroundColor = colorValue(hl.color);
    mark.className = "hl" + (hl.note ? " hl-has-note" : "");
    if (hl.note) mark.title = hl.note;
    else mark.removeAttribute("title");
  });
}

// ---- API -------------------------------------------------------------------

function api(url, method, body) {
  return fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      Accept: "application/json",
    },
    credentials: "same-origin",
    body: body ? JSON.stringify(body) : undefined,
  });
}

function createHighlight(ctx, anchor, color, note) {
  api(ctx.baseUrl, "POST", { highlight: { ...anchor, node_id: ctx.nodeId, color, note } })
    .then((r) => (r.ok ? r.json() : Promise.reject(r)))
    .then((hl) => {
      ctx.byId[hl.id] = hl;
      applyHighlight(ctx, hl);
      closePopover();
    })
    .catch(() => {
      closePopover();
      alert("Sorry, the highlight could not be saved.");
    });
}

function updateHighlight(ctx, hl, changes) {
  api(`${ctx.baseUrl}/${hl.id}`, "PATCH", { highlight: changes })
    .then((r) => (r.ok ? r.json() : Promise.reject(r)))
    .then((fresh) => {
      Object.assign(hl, fresh);
      restyleMarks(ctx, hl);
    })
    .catch(() => {});
}

function deleteHighlight(ctx, hl) {
  api(`${ctx.baseUrl}/${hl.id}`, "DELETE")
    .then((r) => {
      if (!r.ok && r.status !== 204) return Promise.reject(r);
      removeMarks(ctx, hl.id);
      delete ctx.byId[hl.id];
      closePopover();
    })
    .catch(() => {});
}

// ---- wiring ----------------------------------------------------------------

function selectionRangeWithin(root) {
  const sel = window.getSelection();
  if (!sel || sel.isCollapsed || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return null;
  if (!range.toString().trim()) return null;
  return range;
}

function wireContainer(root) {
  const ctx = {
    root,
    nodeId: root.dataset.nodeId,
    baseUrl: root.dataset.highlightUrl,
    signedIn: root.dataset.highlightSignedIn === "true",
    signInUrl: root.dataset.signInUrl || "/users/sign_in",
    byId: {},
    marks: {},
  };

  root.addEventListener("mouseup", () => {
    setTimeout(() => {
      const range = selectionRangeWithin(root);
      if (!range) return;
      const anchor = describeRange(root, range);
      if (!anchor) return;
      showCreatePopover(ctx, range.getBoundingClientRect(), anchor);
    }, 0);
  });

  root.addEventListener("click", (e) => {
    const mark = e.target.closest && e.target.closest("mark.hl");
    if (mark && root.contains(mark)) {
      e.preventDefault();
      e.stopPropagation();
      showEditPopover(ctx, mark);
    }
  });

  return ctx;
}

// Load saved highlights for every container on the page in a single request,
// then re-apply each node's marks. Fetching (rather than embedding) keeps this
// correct inside cached fragments like the book view.
function loadHighlights(contexts) {
  const first = contexts[0];
  if (!first.signedIn || !first.baseUrl) return;
  const ids = contexts.map((c) => c.nodeId).filter(Boolean);
  if (!ids.length) return;

  fetch(`${first.baseUrl}.json?node_ids=${encodeURIComponent(ids.join(","))}`, {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  })
    .then((r) => (r.ok ? r.json() : Promise.reject(r)))
    .then((byNode) => {
      contexts.forEach((ctx) => {
        (byNode[ctx.nodeId] || []).forEach((hl) => {
          ctx.byId[hl.id] = hl;
          applyHighlight(ctx, hl);
        });
      });
    })
    .catch(() => {});
}

function init() {
  injectStyles();
  const roots = Array.from(document.querySelectorAll("[data-highlightable]")).filter(
    (root) => root.dataset.hlReady !== "1"
  );
  if (!roots.length) return;
  const contexts = roots.map((root) => {
    root.dataset.hlReady = "1";
    return wireContainer(root);
  });
  loadHighlights(contexts);
}

// Dismiss the popover when clicking away.
document.addEventListener("mousedown", (e) => {
  if (openPopover && !openPopover.contains(e.target) && !(e.target.closest && e.target.closest("mark.hl"))) {
    closePopover();
  }
});

document.addEventListener("turbo:load", init);
document.addEventListener("DOMContentLoaded", init);
